import React, { useEffect, useState } from "react";
import styles from "./FilterDrawer.module.scss";
import { Drawer } from "antd";
import { filterConsts } from "./constants";
import TNButton from "../../../reusable-components/TNButton";
import {
  ButtonShape,
  ButtonSize,
  ButtonType,
  CommonButtonTexts,
} from "../../../enums/ButtonAttributesEnums";
import FilterForm from "./FilterForm";
import { useFormContext } from "react-hook-form";
import { filterFieldConsts } from "./FilterForm/constants";
import { useJobDatabaseContext } from "../../../context/JobDatabaseFilterContext";
import { PriorityValue } from "../../../Types/jobDatabaseTypes";

interface FilterDrawerProps {
  visible: boolean;
  handleClose: () => void;
  handleSetFilter: () => void;
  clearAllFilterButton: boolean;
  handleClearAllFilter: () => void;
  handleDisableClearAllFilterButton: () => void;
  isAffectedView?: boolean;
}

const FilterDrawer = ({
  visible,
  handleClose,
  handleSetFilter,
  clearAllFilterButton,
  handleClearAllFilter,
  handleDisableClearAllFilterButton,
  isAffectedView = false,
}: FilterDrawerProps) => {
  const { TITLE, WIDTH } = filterConsts;
  const { getValues, setValue } = useFormContext();
  const DrawerHeader = <span className={styles.drawerTitle}>{TITLE}</span>;
  const { jobDatabaseFilters, updateJobDatabaseFilters } =
    useJobDatabaseContext();
  const [forceRerender, triggerForceRerender] = useState<number>(0);
  const [priorityMap, setPriorityMap] = useState<Record<string, PriorityValue>>(
    {
      [filterFieldConsts.JOB_NUMBER]: "unselected",
      [filterFieldConsts.JOB_NAME]: "unselected",
      [filterFieldConsts.CUSTOMER_NAME]: "unselected",
      [filterFieldConsts.JOB_TYPE]: "unselected",
      [filterFieldConsts.EQUIPMENT]: "unselected",
      [filterFieldConsts.MATERIAL_CATEGORY]: "unselected",
      [filterFieldConsts.MATERIAL_TYPE]: "unselected",
      [filterFieldConsts.SALES_PERSON]: "unselected",
    },
  );

  const handleCancel = () => {
    if (
      jobDatabaseFilters?.jobNumber !== getValues(filterFieldConsts.JOB_NUMBER)
    )
      setValue(filterFieldConsts.JOB_NUMBER, jobDatabaseFilters?.jobNumber);
    if (
      jobDatabaseFilters?.customer !==
      getValues(filterFieldConsts.CUSTOMER_NAME)
    )
      setValue(filterFieldConsts.CUSTOMER_NAME, jobDatabaseFilters?.customer);
    if (jobDatabaseFilters?.jobName !== getValues(filterFieldConsts.JOB_NAME))
      setValue(filterFieldConsts.JOB_NAME, jobDatabaseFilters?.jobName);
    if (jobDatabaseFilters?.jobType !== getValues(filterFieldConsts.JOB_TYPE))
      setValue(filterFieldConsts.JOB_TYPE, jobDatabaseFilters?.jobType);
    if (
      jobDatabaseFilters?.equipment !== getValues(filterFieldConsts.EQUIPMENT)
    )
      setValue(filterFieldConsts.EQUIPMENT, jobDatabaseFilters?.equipment);
    if (
      jobDatabaseFilters?.materialCategory !==
      getValues(filterFieldConsts.MATERIAL_CATEGORY)
    )
      setValue(
        filterFieldConsts.MATERIAL_CATEGORY,
        jobDatabaseFilters?.materialCategory,
      );
    if (
      jobDatabaseFilters?.materialType !==
      getValues(filterFieldConsts.MATERIAL_TYPE)
    )
      setValue(
        filterFieldConsts.MATERIAL_TYPE,
        jobDatabaseFilters?.materialType,
      );
    if (
      jobDatabaseFilters?.salesPersonId !==
      getValues(filterFieldConsts.SALES_PERSON)
    )
      setValue(
        filterFieldConsts.SALES_PERSON,
        jobDatabaseFilters?.salesPersonId,
      );
    handleClose();
  };

  useEffect(() => {
    if (!clearAllFilterButton) return;
    setPriorityMap({
      [filterFieldConsts.JOB_NUMBER]: "unselected",
      [filterFieldConsts.JOB_NAME]: "unselected",
      [filterFieldConsts.CUSTOMER_NAME]: "unselected",
      [filterFieldConsts.JOB_TYPE]: "unselected",
      [filterFieldConsts.EQUIPMENT]: "unselected",
      [filterFieldConsts.MATERIAL_CATEGORY]: "unselected",
      [filterFieldConsts.MATERIAL_TYPE]: "unselected",
      [filterFieldConsts.SALES_PERSON]: "unselected",
    });
    handleDisableClearAllFilterButton();
  }, [clearAllFilterButton]);

  const DrawerFooter = (
    <div className={styles.filterButtons}>
      <div>
        <TNButton
          shape={ButtonShape.ROUND}
          size={ButtonSize.LARGE}
          text={CommonButtonTexts.CLEAR_FILTERS}
          ghost
          danger
          onClick={() => {
            triggerForceRerender((prev) => prev + 1);
            Object?.values?.(filterFieldConsts)?.forEach((field) =>
              setValue(field, []),
            );
            handleClearAllFilter();
            handleClose();
          }}
        />
      </div>
      <div className={styles.rightSide}>
        <TNButton
          size={ButtonSize.MEDIUM}
          shape={ButtonShape.DEFAULT}
          type={ButtonType.DEFAULT}
          text={CommonButtonTexts.CANCEL}
          onClick={() => {
            handleCancel();
          }}
        />
        <TNButton
          size={ButtonSize.MEDIUM}
          shape={ButtonShape.DEFAULT}
          type={ButtonType.PRIMARY}
          text={CommonButtonTexts.APPLY}
          onClick={() => {
            const selectedValues = getValues();
            handleSetFilter({
              ...selectedValues,
            });
            handleClose();
          }}
        />
      </div>
    </div>
  );

  return (
    <Drawer
      visible={visible}
      onClose={handleCancel}
      maskClosable={false}
      width={WIDTH}
      className={styles.jobDatabaseFilter}
      title={DrawerHeader}
      footer={DrawerFooter}
      key={forceRerender}
    >
      <FilterForm
        visible={visible}
        priorityMap={priorityMap}
        setPriorityMap={setPriorityMap}
        isAffectedView={isAffectedView}
      />
    </Drawer>
  );
};

export default FilterDrawer;
